/**
 * Fills Mid America's "Condo Questionnaire — 2-4 Unit Projects".
 *
 * Same posture as the 1076 fill: the bank receives its own PDF with the
 * AcroForm written and then flattened. The one twist of this form is that its
 * Yes/No answers are PAIRS of independent checkboxes (no radio groups), so a
 * stored "Yes" ticks one box and a stored "No" ticks its sibling — and the
 * delinquency question stores a COUNT, where anything above zero means Yes.
 */

import { PDFArray, PDFDict, PDFDocument, PDFName, PDFTextField, StandardFonts } from "pdf-lib";
import type { PDFForm } from "pdf-lib";

import { PDF_FILE, allFields } from "./spec.mjs";

export interface FillContext {
  [key: string]: string | null | undefined;
}

export interface FillReport {
  text: number;
  boxes: number;
  problems: string[];
}

export interface FillOptions {
  pdfBytes?: ArrayBuffer | Uint8Array;
  pdfUrl?: string;
  flatten?: boolean;
}

interface SpecField {
  name: string;
  type?: string;
  pdf?: string | string[] | { checks?: { Yes: string; No: string }; yesWhen?: string };
}

function isYes(value: unknown): boolean {
  return /^(yes|y|true|on|1)$/i.test(String(value ?? "").trim());
}

function isNo(value: unknown): boolean {
  return /^(no|n|false|off|0)$/i.test(String(value ?? "").trim());
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(value).trim();
}

function fontSizeFor(value: string): number | null {
  if (value.length > 90) return 6;
  if (value.length > 55) return 7;
  if (value.length > 35) return 8;
  return null;
}

export function defaultPdfUrl(): string {
  const base = (import.meta as any).env?.BASE_URL ?? "/";
  return `${base.endsWith("/") ? base : base + "/"}forms/${PDF_FILE}`;
}

/**
 * The form tree hangs all 55 fields off one parent node that has no name at
 * all. pdf-lib then reports names like "undefined.Seller Name" — and its
 * flatten() crashes trying to remove the nameless parent. Re-hang the fields
 * directly off the AcroForm root before doing anything: every field becomes
 * top-level under its own plain name.
 */
function adoptNamelessKids(document: PDFDocument) {
  const acro = document.getForm().acroForm;
  const fields = acro.dict.lookupMaybe(PDFName.of("Fields"), PDFArray);
  if (!fields) return;
  for (let i = 0; i < fields.size(); i++) {
    const dict = fields.lookup(i, PDFDict);
    if (!dict.has(PDFName.of("T"))) continue;
    const parent = dict.lookupMaybe(PDFName.of("Parent"), PDFDict);
    if (parent && !parent.has(PDFName.of("T"))) {
      dict.delete(PDFName.of("Parent"));
    }
  }
}

function writeText(form: PDFForm, target: string, value: string) {
  const field = form.getTextField(target);
  const size = fontSizeFor(value);
  if (size) field.setFontSize(size);
  field.setText(value);
}

/** Which box of the pair a stored value ticks, or null to leave both blank. */
function pickBox(field: SpecField, value: string): "Yes" | "No" | null {
  const pdf = field.pdf as { yesWhen?: string };
  if (pdf.yesWhen === "nonzero") {
    const n = Number(value.replace(/[^0-9.-]/g, ""));
    if (Number.isFinite(n)) return n > 0 ? "Yes" : "No";
    return isYes(value) ? "Yes" : isNo(value) ? "No" : null;
  }
  if (isYes(value)) return "Yes";
  if (isNo(value)) return "No";
  return null;
}

export async function fillMidAmerica(
  answers: Record<string, string | null | undefined>,
  _context: FillContext = {},
  options: FillOptions = {},
): Promise<{ bytes: Uint8Array; report: FillReport }> {
  let pdfBytes = options.pdfBytes;
  if (!pdfBytes) {
    const url = options.pdfUrl ?? defaultPdfUrl();
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Could not load the blank form from ${url} (${response.status})`);
    }
    pdfBytes = await response.arrayBuffer();
  }

  const document = await PDFDocument.load(pdfBytes);
  adoptNamelessKids(document);
  const form = document.getForm();
  const report: FillReport = { text: 0, boxes: 0, problems: [] };

  for (const field of allFields() as SpecField[]) {
    const raw = answers[field.name];
    if (raw === null || raw === undefined || String(raw).trim() === "") continue;
    const value = field.type === "date" ? formatDate(String(raw)) : String(raw).trim();

    const pdf = field.pdf;
    if (!pdf) continue;

    try {
      if (typeof pdf === "object" && !Array.isArray(pdf) && pdf.checks) {
        const side = pickBox(field, value);
        if (side === null) continue;
        form.getCheckBox(pdf.checks[side]).check();
        report.boxes++;
        // The delinquency count also prints nowhere as text on this form;
        // the box is its whole answer.
      } else {
        const targets = typeof pdf === "string" ? [pdf] : (pdf as string[]);
        for (const target of targets) {
          writeText(form, target, value);
          report.text++;
        }
      }
    } catch (error) {
      report.problems.push(`${field.name}: ${(error as Error).message}`);
    }
  }

  if (options.flatten !== false) {
    // Same as the 1076: text appearances regenerated in Helvetica (the blank
    // form ships none), boxes left to their own drawings, then baked in.
    const helvetica = await document.embedFont(StandardFonts.Helvetica);
    for (const field of form.getFields()) {
      if (field instanceof PDFTextField) field.updateAppearances(helvetica);
    }
    form.flatten({ updateFieldAppearances: false });
  }

  const bytes = await document.save();
  return { bytes, report };
}
